import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { summaryCardTheme as t } from "./styles";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";

const formatCurrency = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : "$0.00";

const getTotalFailedControls = (overall) => {
  if (!overall || !Array.isArray(overall.compliance_by_framework)) return overall?.compliance_failed_controls_total ?? 0;
  return overall.compliance_by_framework.reduce(
    (sum, fw) => sum + (Number(fw.controls_failed_total) || 0),
    0
  );
};

const kpiConfig = (overall) => [
  {
    label: "Total ARNs",
    value: overall?.arns_total ?? 0,
    tooltip: "Total number of ARNs scanned across all services",
  },
  {
    label: "Security Findings",
    value: overall?.arns_security_failed ?? 0,
    tooltip: "ARNs with failed security checks (CSPM)",
    accent: "issue",
  },
  {
    label: "Control ID Findings",
    value: getTotalFailedControls(overall),
    tooltip: "Total failed compliance controls across all frameworks",
    accent: "warning",
  },
  {
    label: "Compliance Framework",
    value: overall?.compliance_frameworks_failed_count ?? null,
    tooltip: "Number of compliance frameworks with at least one failed control",
    emptyLabel: "—",
    frameworks: Array.isArray(overall?.compliance_frameworks_failed)
      ? overall.compliance_frameworks_failed
      : [],
  },
  {
    label: "Cloud Waste",
    value: overall?.arns_cloud_waste ?? 0,
    tooltip: "ARNs eligible for cost savings",
    accent: "success",
  },
  {
    label: "Cost (Last 30 Days)",
    value: overall?.cost_unblended_mtd_total ?? 0,
    formatter: formatCurrency,
    tooltip: "Unblended cost for the last 30 days across scanned ARNs",
    accent: "success",
  },
  {
    label: "Security+Compliance Finding",
    value: overall?.arns_both ?? 0,
    tooltip: "ARNs common to CSPM (security) and Compliance findings",
    accent: "issue",
  },
  {
    label: "Security+Compliance+Cost Findings",
    value: overall?.arns_all_three ?? 0,
    tooltip: "ARNs common to CSPM, Compliance and Cost Insight findings",
    accent: "issue",
  },
  {
    label: "Security+Compliance+Cost+Cloud Waste",
    value: overall?.arns_all_four ?? 0,
    tooltip: "ARNs common to CSPM, Compliance, Cost Insight and Cloud Waste",
    accent: "issue",
  },
];

function SummaryKpiCards({ overall, compact }) {
  const items = kpiConfig(overall);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
          // In compact (dashboard strip) view, show all KPIs in a single row on large screens.
          // In full dialog view, use multiple rows for better readability and responsiveness.
          lg: compact ? "repeat(9, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
          xl: compact ? "repeat(9, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))",
        },
        gap: compact ? 1 : 2,
        fontFamily: t.fontFamily,
      }}
    >
      {items.map((item) => {
        const hasValue = item.value != null && item.value !== "" && (typeof item.value !== "number" || !Number.isNaN(item.value));
        const displayValue =
          item.emptyLabel != null && !hasValue
            ? item.emptyLabel
            : typeof item.formatter === "function"
            ? item.formatter(item.value)
            : item.value;
        const color =
          item.accent === "issue"
            ? t.colors.issue
            : item.accent === "warning"
            ? t.colors.warning
            : item.accent === "success"
            ? t.colors.success
            : t.colors.text;

        const hasFrameworkList = item.frameworks && item.frameworks.length > 0;
        const shouldFrameworkListScroll = hasFrameworkList && item.frameworks.length > 4;

        const tooltipContent = hasFrameworkList ? (
            <Box
              sx={{
                p: 1.5,
                maxWidth: "min(320px, 80vw)",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: t.fontFamily,
                  fontWeight: 700,
                  mb: 0.25,
                  fontSize: "0.8rem",
                  color: "text.primary",
                }}
              >
                Compliance Framework
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: t.fontFamily,
                  fontSize: "0.72rem",
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Frameworks that currently have one or more failed controls:
              </Typography>
              <Box
                sx={{
                  maxHeight: shouldFrameworkListScroll ? 180 : "none",
                  overflowY: shouldFrameworkListScroll ? "auto" : "visible",
                  overflowX: "hidden",
                  pr: shouldFrameworkListScroll ? 0.5 : 0,
                }}
              >
                {item.frameworks.map((fw) => {
                  const iconSrc = getComplianceIcon(fw);
                  return (
                    <Box
                      key={fw}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0.5,
                        gap: 1,
                      }}
                    >
                      {iconSrc && (
                        <Box
                          component="img"
                          src={iconSrc}
                          alt={fw}
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            objectFit: "contain",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: t.fontFamily,
                          fontSize: "0.72rem",
                          color: "text.primary",
                        }}
                      >
                        {fw}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : item.tooltip;

        return (
          <Box
            key={item.label}
            sx={{
              position: "relative",
              backgroundColor: compact ? "transparent" : t.colors.card,
              border: compact ? "none" : `1px solid ${t.colors.border}`,
              borderRadius: t.radius.lg,
              p: compact ? 0.5 : 2,
              boxShadow: compact ? "none" : t.shadow,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              "&:hover": compact
                ? {}
                : {
                    borderColor: t.colors.borderLight,
                    boxShadow:
                      "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
                  },
            }}
          >
            {/* Superscript info icon (hover target only) */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.35, mb: compact ? 0 : 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: t.colors.textMuted,
                  fontSize: compact ? "0.6rem" : "0.72rem",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 0,
                  fontFamily: t.fontFamily,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.15,
                }}
              >
                {item.label}
              </Typography>

              <Tooltip
                title={tooltipContent}
                arrow
                placement="top"
                enterDelay={200}
                leaveDelay={0}
                PopperProps={{
                  modifiers: [
                    {
                      name: "preventOverflow",
                      options: { boundary: "viewport", padding: 8 },
                    },
                    {
                      name: "flip",
                      options: { fallbackPlacements: ["top", "bottom", "right", "left"] },
                    },
                  ],
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: 3,
                      pointerEvents: "auto",
                      maxWidth: "min(320px, 80vw)",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      maxHeight: "none",
                      // Prevent scrollbars in the tooltip shell for short content.
                      // If content needs scrolling, it should happen in inner containers only.
                      overflow: "hidden",
                      overflowX: "hidden",
                      "& .MuiTooltip-arrow": {
                        color: "background.paper",
                        "&::before": {
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  },
                }}
              >
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label={`${item.label} info`}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: t.colors.textMuted,
                    transform: "translateY(-6px)",
                    "&:hover": { color: t.colors.text },
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ fontSize: compact ? 14 : 16 }} />
                </Box>
              </Tooltip>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color,
                fontWeight: 700,
                fontSize: compact ? "0.9375rem" : "1.25rem",
                fontFamily: t.fontFamily,
              }}
            >
              {displayValue}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default SummaryKpiCards;
